import { SystemWorkOrderStatus, WorkOrderResourceStatus, WorkOrderStatusSystemName } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { BadgeTint } from './badge.tint';

const tokens = DesignSystem.tokens;

export const statusTintStyleId = 'manage-job-pro-status-tint-theme';

// Figma Resources List badge recipe: tinted bordered chip — {family}-50 wash, {family}-400 border,
// {family}-700 text. Deliberately the stronger -400 border, not the -200 the Flags/Related-Links
// chips use. Ramp steps (not semantic tokens) are the sanctioned choice for tag tints, and each
// step resolves to a theme-remapped --wb-* variable, so the pills follow a theme change — which
// the runtime badgeTint() color-mix(… #fff) recipe never could.
//
// Shared by the Resources card pills, the Stages list status chips, the Related Links job status
// tags, the stage Details status tag and the header strip's Job Status chip — every host
// component (or the statusColor pipe serving it) mounts this bridge (Mount dedupes by id), and
// all selectors carry the full family set so a tint computed in any component resolves the same.
const statusTintProperties = {
  '--local-mjp-status-success-bg': tokens.componentColors.utility.success50,
  '--local-mjp-status-success-border': tokens.componentColors.utility.success400,
  '--local-mjp-status-success-text': tokens.componentColors.utility.success700,
  '--local-mjp-status-brand-bg': tokens.componentColors.utility.brand50,
  '--local-mjp-status-brand-border': tokens.componentColors.utility.brand400,
  '--local-mjp-status-brand-text': tokens.componentColors.utility.brand700,
  '--local-mjp-status-warning-bg': tokens.componentColors.utility.warning50,
  '--local-mjp-status-warning-border': tokens.componentColors.utility.warning400,
  '--local-mjp-status-warning-text': tokens.componentColors.utility.warning700,
  // warning100: the DS has no yellow family, so the terminal states (Closed/Canceled) borrow the
  // warning hue with a deeper -100 wash to stay tellable from In Progress's -50.
  '--local-mjp-status-warning100-bg': tokens.componentColors.utility.warning100,
  '--local-mjp-status-warning100-border': tokens.componentColors.utility.warning400,
  '--local-mjp-status-warning100-text': tokens.componentColors.utility.warning700,
  '--local-mjp-status-error-bg': tokens.componentColors.utility.error50,
  '--local-mjp-status-error-border': tokens.componentColors.utility.error400,
  '--local-mjp-status-error-text': tokens.componentColors.utility.error700,
  '--local-mjp-status-indigo-bg': tokens.componentColors.utility.indigo50,
  '--local-mjp-status-indigo-border': tokens.componentColors.utility.indigo400,
  '--local-mjp-status-indigo-text': tokens.componentColors.utility.indigo700,
  '--local-mjp-status-gray-bg': tokens.componentColors.utility.gray50,
  '--local-mjp-status-gray-border': tokens.componentColors.utility.gray400,
  '--local-mjp-status-gray-text': tokens.componentColors.utility.gray700,
};

export const statusTintBridgeStyles = {
  // editor-header-v2 and the job Gantt header lift the Status chip beside the job number, outside the strip's scope.
  // The -v2 hosts are separate elements, not variants of the ones beside them: a pill inside
  // stage-resources-card-v2 or workorder-details-v2 inherits nothing from stage-resources-card or
  // workorder-details, so leaving them out left every tint var undefined and every pill on w-tag's
  // default outline.
  // The native Gantt task tooltip renders in a body-level popup, outside every host above.
  'stage-resources-card, stage-resources-card-v2, workorder-list-item, related-links-card, workorder-details, workorder-details-v2, job-header-chips, editor-header-v2, job-gantt-native-header, tooltip.gantt-summary-tooltip': statusTintProperties,
};

type StatusFamily = 'success' | 'brand' | 'warning' | 'warning100' | 'error' | 'indigo' | 'gray';

// Assigned/Completed = success and Dispatched = brand are exact from the Figma frame; the
// in-motion trio keeps its former orange grouping as warning; On Hold reads as paused/neutral.
const RESOURCE_STATUS_FAMILY: Record<WorkOrderResourceStatus, StatusFamily> = {
  [WorkOrderResourceStatus.Assigned]: 'success',
  [WorkOrderResourceStatus.Dispatched]: 'brand',
  [WorkOrderResourceStatus.Enroute]: 'warning',
  [WorkOrderResourceStatus.OnSite]: 'warning',
  [WorkOrderResourceStatus.CheckedOut]: 'warning',
  [WorkOrderResourceStatus.Completed]: 'success',
  [WorkOrderResourceStatus.OnHold]: 'gray',
};

// The Resources card contractor row has no resource record — its pill reads the stage's own
// status, mirroring the resource families for the like-named statuses (so Dispatched splits into
// brand here too).
const STAGE_STATUS_FAMILY: Record<string, StatusFamily> = {
  [WorkOrderStatusSystemName.Assigned]: 'success',
  [WorkOrderStatusSystemName.Dispatched]: 'brand',
  [WorkOrderStatusSystemName.SubcontractorDispatched]: 'brand',
  [WorkOrderStatusSystemName.Completed]: 'success',
};

// Stages-list chip families, hue-matched to the legacy getSystemColor() palette this replaces:
// Open green → success, In Progress orange → warning, On Hold red → error, Completed blue → brand,
// Finalised dark blue → indigo (keeps it distinct from Completed). The two terminal states keep a
// yellow read (legacy Closed was pale yellow) via the warning100 variant above.
const SYSTEM_STATUS_FAMILY: Record<SystemWorkOrderStatus, StatusFamily> = {
  [SystemWorkOrderStatus.Open]: 'success',
  [SystemWorkOrderStatus.InProgress]: 'warning',
  [SystemWorkOrderStatus.OnHold]: 'error',
  [SystemWorkOrderStatus.Completed]: 'brand',
  [SystemWorkOrderStatus.Finalised]: 'indigo',
  [SystemWorkOrderStatus.Closed]: 'warning100',
  [SystemWorkOrderStatus.Canceled]: 'warning100',
};

// var() references resolve on the w-tag/w-badge element because it applies these as inline styles
// and the custom properties inherit down from the bridge's host-element scope.
function familyTint(family: StatusFamily): BadgeTint {

  return {
    text: `var(--local-mjp-status-${family}-text)`,
    background: `var(--local-mjp-status-${family}-bg)`,
    border: `var(--local-mjp-status-${family}-border)`,
  };
}

export function resourceStatusTint(status: WorkOrderResourceStatus): BadgeTint {

  return familyTint(RESOURCE_STATUS_FAMILY[status] ?? 'gray');
}

export function stageStatusTint(systemName: string): BadgeTint {

  return familyTint(STAGE_STATUS_FAMILY[systemName] ?? 'warning');
}

export function systemStatusTint(status: SystemWorkOrderStatus): BadgeTint {

  return familyTint(SYSTEM_STATUS_FAMILY[status] ?? 'gray');
}
